import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';

import api from '../../services/api';
import { fetchCEP } from '../../services/viacep';
import { useUtm } from '../../hooks/useUtm';
import { maskCPF, maskPhone, maskDate, maskCEP, validateCPF } from '../../hooks/useMask';

import Field from '../../components/Field/Field';
import Input from '../../components/Input/Input';
import { PrimaryButton } from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import Loader from '../../components/Loader/Loader';

import styles from './Home.module.css';

/* ─── initial errors shape ─── */
const EMPTY_ERRORS = {
  nome: '', cpf: '', nasc: '', wpp: '', cep: '',
};

export default function Home() {
  const navigate  = useNavigate();
  const utms      = useUtm();
  const [cookies] = useCookies(['__gtm_campaign_url']);

  const [form, setForm] = useState({
    nome: '', cpf: '', nasc: '', wpp: '', cep: '',
  });
  const [errors,  setErrors]  = useState(EMPTY_ERRORS);
  const [alert,   setAlert]   = useState('');
  const [loading, setLoading] = useState(false);
  const [cepMsg,  setCepMsg]  = useState('');

  /* ── address state (pre-filled via CEP, sent on page 2) ── */
  const [address, setAddress] = useState({
    logradouro: '', bairro: '', cidade: '', uf: '',
  });

  const cepRef = useRef();

  /* ── helpers ── */
  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  }

  /* ── CEP lookup ── */
  async function handleCepBlur() {
    const raw = form.cep.replace(/\D/g, '');
    if (raw.length !== 8) return;
    setCepMsg('Buscando CEP...');
    try {
      const data = await fetchCEP(raw);
      setAddress(data);
      setCepMsg('✓ CEP encontrado: ' + data.cidade + ' / ' + data.uf);
      // persist cidade e uf para o submit e para página 2
      sessionStorage.setItem('address_prefill', JSON.stringify(data));
      sessionStorage.setItem('cep_raw',   raw);
      sessionStorage.setItem('cep_cidade', data.cidade);
      sessionStorage.setItem('cep_uf',     data.uf);
    } catch {
      setCepMsg('CEP não encontrado. Verifique e tente novamente.');
      sessionStorage.removeItem('cep_cidade');
      sessionStorage.removeItem('cep_uf');
    }
  }

  /* ── validation ── */
  function validate() {
    const e = { ...EMPTY_ERRORS };
    let ok = true;

    if (!form.nome.trim() || !form.nome.trim().includes(' ')) {
      e.nome = 'Insira seu nome completo (nome e sobrenome)'; ok = false;
    }
    if (!validateCPF(form.cpf)) {
      e.cpf = 'CPF inválido. Verifique os dígitos.'; ok = false;
    }
    const nascClean = form.nasc.replace(/\D/g, '');
    if (nascClean.length !== 8) {
      e.nasc = 'Insira uma data válida'; ok = false;
    }
    const wppClean = form.wpp.replace(/\D/g, '');
    if (wppClean.length < 10) {
      e.wpp = 'Número inválido. Inclua o DDD.'; ok = false;
    }
    const cepClean = form.cep.replace(/\D/g, '');
    if (cepClean.length !== 8) {
      e.cep = 'CEP inválido'; ok = false;
    }

    setErrors(e);
    return ok;
  }

  /* ── UTM cookie parser ── */
  function parseCookieUtm() {
    const raw = cookies.__gtm_campaign_url || '';
    const p   = new URLSearchParams(raw.split('?')[1] || '');
    const keys = ['utm_source','utm_medium','utm_campaign','utm_id',
      'utm_content','utm_term','gclid','fbclid','aff_click_id'];
    return keys.reduce((acc, k) => { acc[k] = p.get(k) || ''; return acc; }, {});
  }

  /* ── aff_click_id tracking ── */
  function trackAff(id) {
    if (!id) return;
    axios.get(`https://api.vsboon.com.br/reward/delivery/savvi?aff_click_id=${id}&action_type=Inapp`)
        .catch(() => {});
  }

  /* ── submit ── */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    const cpf        = form.cpf.replace(/\D/g, '');
    const celular    = form.wpp.replace(/\D/g, '');
    const dt_nasc    = form.nasc; // DD/MM/YYYY
    const cep        = form.cep.replace(/\D/g, '');
    const nome       = form.nome.trim();
    const cidade     = sessionStorage.getItem('cep_cidade') || address.cidade || '';
    const uf         = sessionStorage.getItem('cep_uf')     || address.uf     || '';
    const cookieUtms = parseCookieUtm();

    if (cookieUtms.aff_click_id) trackAff(cookieUtms.aff_click_id);

    const body = {
      nome, cpf, cep, cidade, uf,
      dt_nascimento: dt_nasc,
      celular,
      email: '',
      ...cookieUtms,
      ...utms,
    };

    // Salva dados imediatamente — sem esperar a API
    sessionStorage.setItem('nomeUsuario',   nome);
    sessionStorage.setItem('payload_phone', celular);
    sessionStorage.setItem('cep_raw',       cep);

    // Dispara o create SEM await — guarda a Promise globalmente
    // A página 2 vai aguardar essa Promise antes de fazer o update
    window.__createPromise = api.post('/leads/create', body)
        .then(({ data }) => {
          sessionStorage.setItem('encode_data', data.encode_data);
          sessionStorage.setItem('id',          data.id || '');
          sessionStorage.setItem('create_cod_erro', String(data?.cod_erro ?? ''));

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'fgts-form-success',
              email: '',
              phone_number: `+55${celular}`,
              'address.first_name': nome.split(' ')[0],
              'address.last_name':  nome.split(' ')[1] || '',
              'address.country': 'Br',
            });
          }

          return data;
        })
        .catch((err) => {
          window.__createError = err?.response?.data?.message
              || err?.response?.data?.detail
              || 'Erro ao processar. Tente novamente.';
          throw err;
        });

    // Navega imediatamente para página 2 sem esperar a API
    navigate('/endereco');
  }

  return (
      <>
        {loading && <Loader text="Verificando seus dados..." />}

        <div className={styles.wrap}>
          <h1 className={styles.title}>
            Preencha com seus dados pessoais
          </h1>
          <p className={styles.sub}>
            Rápido, seguro e sem burocracia — análise em instantes.
          </p>

          <form onSubmit={handleSubmit} noValidate className={styles.form}>

            <Field label="Nome completo" id="nome" error={errors.nome}>
              <Input
                  id="nome"
                  type="text"
                  placeholder="Nome completo"
                  autoComplete="name"
                  autoFocus
                  hasError={!!errors.nome}
                  value={form.nome}
                  onChange={e => set('nome', e.target.value)}
              />
            </Field>

            <Field label="CPF" id="cpf" error={errors.cpf}>
              <Input
                  id="cpf"
                  type="text"
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  hasError={!!errors.cpf}
                  value={form.cpf}
                  onChange={e => set('cpf', maskCPF(e.target.value))}
              />
            </Field>

            <Field label="Data de nascimento" id="nasc" error={errors.nasc}>
              <Input
                  id="nasc"
                  type="text"
                  placeholder="00/00/0000"
                  inputMode="numeric"
                  hasError={!!errors.nasc}
                  value={form.nasc}
                  onChange={e => set('nasc', maskDate(e.target.value))}
              />
            </Field>

            <Field label="WhatsApp (com DDD)" id="wpp" error={errors.wpp}>

              <Input
                  id="wpp"
                  type="text"
                  placeholder="(00) 00000-0000"
                  inputMode="numeric"
                  hasError={!!errors.wpp}
                  value={form.wpp}
                  onChange={e => set('wpp', maskPhone(e.target.value))}
              />
            </Field>

            <Field label="CEP" id="cep" error={errors.cep}>
              <Input
                  id="cep"
                  ref={cepRef}
                  type="text"
                  placeholder="00000-000"
                  inputMode="numeric"
                  hasError={!!errors.cep}
                  value={form.cep}
                  onChange={e => set('cep', maskCEP(e.target.value))}
                  onBlur={handleCepBlur}
              />
              {cepMsg && <p className={styles.cepMsg}>{cepMsg}</p>}
            </Field>

            {alert && <Alert>{alert}</Alert>}

            <PrimaryButton type="submit" loading={loading}>
              Solicitar Simulação
            </PrimaryButton>

            <div className={styles.secure}>
              <svg width="13" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="5" y="11" width="14" height="11" rx="2"
                      stroke="#9CA3AF" strokeWidth="2" />
                <path d="M8 11V7a4 4 0 018 0v4"
                      stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Você está em um ambiente seguro e criptografado.
            </div>
          </form>
        </div>
      </>
  );
}